/* ═══════════════════════════════════════════════════════
   NYC Suite — Utility Functions
   ═══════════════════════════════════════════════════════ */

import { hasCustomWording, normalizePrintWording } from "./printWording.js";
import { VOLUME_TIERS, SECTION_DEFS, STUDIO_ADDRESS, STUDIO_ADDRESS_PARTS, SESSION_TYPES, DEFAULT_SESSION_TYPE, SESSION_LOCATION_FIELDS } from "./constants.js";
import { resolveClient, resolveContact } from "./identity.js";
import { readClient } from "./quoteParty.js";
import { BULK_ELIGIBLE } from "./consent.js";
/* campaigns.js imports consent.js and nothing else, so this is a leaf, not a
   cycle. The id scheme lives there because that is where every other campaign
   rule lives, and because it is the one this file used to disagree with. */
import { makeCampaignId } from "./campaigns.js";

/* ═══ FONTS ═══ */
export const fl = "'DM Sans', system-ui, -apple-system, sans-serif";
export const fb = "'DM Sans', system-ui, -apple-system, sans-serif";
export const fm = "'DM Mono', 'SF Mono', monospace";

/* ═══ FORMATTING ═══ */
export const fmt = (n) => "$" + Number(n).toLocaleString("en-US");

/* Money for quotes (the Quote Builder, the quote PDF, the estimators and the
   quote notices). Whole dollars print whole ("$4,000"); anything with cents
   prints exactly two digits ("$757.50", never "$757.5"). Rounds to the cent
   first, the same way calcTotals rounds its total (Math.round(x * 100) / 100),
   so a printed line or subtotal never disagrees with the total by a cent, and
   float noise (4292.4999999) or a sum that lands on a whole dollar
   (99.999 -> "$100") reads cleanly. Negatives use the typographic minus
   ("−$50"), the sign the quote PDF prints for discounts. A non-number prints
   "—" rather than "$NaN"; null counts as 0, as it did with fmt.
   `maxDecimals` above 2 (at most 6) is for a unit rate typed with sub-cent
   precision ($0.125 a sq ft): such a rate keeps its digits so qty × rate
   still reads true; a rate whose extra digits round away prints as above.
   Too large to scale exactly: the sub-cent digits are dropped and the value
   prints to the cent; too large for that, it prints whole.
   Pass a discount as a negative (fmtMoney(-amount)) rather than putting a
   minus in front, so one that rounds to $0 prints "$0", not "−$0".
   Display only: never use it to build text that gets saved.
   `fmt` stays as it was for the CRM, Dashboard, Insights, Jobs list and job
   Financials screens. */
// Signed Math.round(x * 10^digits), as calcTotals rounds; null when the scaled
// number is past exact integers (the caller then uses the coarser precision).
const scaledRound = (x, digits) => {
  const s = Math.round(x * 10 ** digits);
  return Number.isSafeInteger(s) ? s : null;
};
export const fmtMoney = (n, { maxDecimals = 2 } = {}) => {
  const v = Number(n);
  if (!Number.isFinite(v)) return "—";
  const extra = Math.min(6, Math.max(2, Math.floor(maxDecimals) || 2));
  const fine = extra > 2 && !Number.isInteger(v) ? scaledRound(v, extra) : null;
  const cents = Number.isInteger(v) ? null : scaledRound(v, 2);
  let shown, digits;
  if (fine !== null && fine % 10 ** (extra - 2) !== 0) {
    shown = fine / 10 ** extra; digits = { minimumFractionDigits: 2, maximumFractionDigits: extra };
  } else if (cents !== null && cents % 100 !== 0) {
    shown = cents / 100; digits = { minimumFractionDigits: 2, maximumFractionDigits: 2 };
  } else {
    shown = cents !== null ? cents / 100 : Math.round(v); digits = { maximumFractionDigits: 0 };
  }
  const text = Math.abs(shown).toLocaleString("en-US", digits);
  return `${shown < 0 && /[1-9]/.test(text) ? "−" : ""}$${text}`;
};

/* 24hr "HH:MM" → 12hr "h:MM AM/PM" */
export const fmtTime = (t) => {
  if (!t) return "—";
  const [hh, mm] = t.split(":");
  const h = parseInt(hh, 10);
  if (isNaN(h)) return t;
  const suffix = h >= 12 ? "PM" : "AM";
  const h12 = h === 0 ? 12 : h > 12 ? h - 12 : h;
  return `${h12}:${mm} ${suffix}`;
};

/* "YYYY-MM-DD" → formatted local date. Empty string on falsy input.
   Import the FMT_DATE_* constants below rather than passing raw strings —
   they autocomplete and catch typos at lint/type time. */
export const FMT_DATE_SHORT = "short"; // "Apr 23"
export const FMT_DATE_FULL  = "full";  // "Apr 23, 2026"
export const FMT_DATE_LONG  = "long";  // "Thu, Apr 23, 2026"
export const FMT_DATE_DOW   = "dow";   // "Thu, Apr 23"
const FMT_DATE_OPTS = {
  [FMT_DATE_SHORT]: { month: "short", day: "numeric" },
  [FMT_DATE_FULL]:  { month: "short", day: "numeric", year: "numeric" },
  [FMT_DATE_LONG]:  { weekday: "short", month: "short", day: "numeric", year: "numeric" },
  [FMT_DATE_DOW]:   { weekday: "short", month: "short", day: "numeric" },
};
export const fmtDate = (iso, style = FMT_DATE_SHORT) => {
  if (!iso) return "";
  const opts = FMT_DATE_OPTS[style];
  if (!opts) {
    if (import.meta.env?.DEV) throw new Error(`fmtDate: unknown style "${style}". Use FMT_DATE_SHORT / _FULL / _LONG / _DOW.`);
    return "";
  }
  // Construct via (y, m-1, d) rather than `new Date(iso)` to avoid the UTC-midnight-shifts-to-previous-day bug in negative timezones.
  const [y, m, d] = iso.split("-").map(Number);
  return new Date(y, m - 1, d).toLocaleDateString("en-US", opts);
};

/* Strict YYYY-MM-DD check, "" for anything else. Legacy quote data carries junk in
   date fields (the original quoter wrote scanDate:"TBD") — list/sort/grouping code
   must treat those as undated, never feed them to Date(). */
export const isISODateOnly = (v) => /^\d{4}-\d{2}-\d{2}$/.test(String(v || ""));
export const isoDateOrEmpty = (v) => (isISODateOnly(v) ? String(v) : "");

// Initials for an avatar chip: first letters of the first two words, "?" fallback.
export const initials = (name) =>
  ((name || "").trim().split(/\s+/).map(w => w[0]).slice(0, 2).join("") || "?").toUpperCase();

/* ═══ ID GENERATION ═══ */
export const mkClientCode = (name) => {
  if (!name) return "";
  // Strip non-alphanumeric, split into words
  const words = name.trim().replace(/[^a-zA-Z0-9\s]/g, "").split(/\s+/).filter(Boolean);
  if (words.length === 0) return "";
  // Strip common suffixes (LLC, Inc, Co, Corp, etc.)
  const suffixes = ["llc", "inc", "co", "corp", "ltd", "lp", "plc", "gmbh", "pllc"];
  const clean = words.filter(w => !suffixes.includes(w.toLowerCase()));
  const w = clean.length > 0 ? clean : words;
  // Single word: first 6 chars. Two+ words: 3 from first + 3 from second.
  if (w.length === 1) return w[0].toLowerCase().slice(0, 6);
  const first = w[0].toLowerCase().slice(0, 3);
  const second = w[1].toLowerCase().slice(0, 3);
  return first + second;
};

export const mkJobId = (clientCode, dateStr) => {
  if (!clientCode) return "";
  let d = dateStr;
  if (!d || d === "TBD") {
    const now = new Date();
    d = `${String(now.getFullYear()).slice(2)}${String(now.getMonth()+1).padStart(2,"0")}${String(now.getDate()).padStart(2,"0")}`;
  } else {
    // Parse YYYY-MM-DD string directly to avoid timezone shift
    const parts = d.split("-");
    if (parts.length === 3) d = `${parts[0].slice(2)}${parts[1]}${parts[2]}`;
  }
  return `${clientCode}_${d}`;
};

export const mkFile = (client, date) => {
  const c = (client || "unknown").toLowerCase().replace(/[^a-z0-9]+/g, "").slice(0, 30);
  const d = (date || new Date().toISOString().split("T")[0]).replace(/-/g, "").slice(2);
  return `nyc_${c}_${d}`;
};

// Math.random fallback is only for non-browser JS contexts (tests, SSR) where crypto.randomUUID is missing.
export const mkUuid = () => {
  try {
    if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
      return crypto.randomUUID().replace(/-/g, "").slice(0, 10);
    }
  } catch (_) { /* crypto.randomUUID unavailable — fall through to Math.random */ }
  return Math.random().toString(16).slice(2, 12).padEnd(10, "0");
};

export const mkSessionId = () => `sess_${mkUuid()}`;

/* ═══ BUSINESS DAYS ═══ */
// LOCAL calendar day as YYYY-MM-DD. toISOString() is UTC — during a US evening
// it reads as tomorrow, so date-only fields serialized through it land a day
// late. Use this for any date-only write.
export const localDayISO = (d = new Date()) => {
  const p = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
};

export const addBizDays = (n) => {
  const d = new Date();
  let added = 0;
  while (added < n) {
    d.setDate(d.getDate() + 1);
    const dow = d.getDay();
    if (dow !== 0 && dow !== 6) added++;
  }
  return localDayISO(d);
};

/* ═══ VOLUME PRICING ═══ */
export const getTierRate = (type, qty) => {
  const tiers = VOLUME_TIERS[type];
  if (!tiers || !qty || qty < 1) return (type === "body_scan" ? 1200 : 900);
  const t = tiers.find(t => qty >= t.min && qty <= t.max);
  return t ? t.rate : tiers[tiers.length - 1].rate;
};

export const getTierLabel = (type, qty) => {
  const tiers = VOLUME_TIERS[type];
  if (!tiers) return "";
  const t = tiers.find(t => qty >= t.min && qty <= t.max);
  if (!t) return "";
  if (t.max === Infinity) return `${t.min}+`;
  if (t.min === t.max) return `${t.min}`;
  return `${t.min}–${t.max}`;
};

export const hasScanItems = (items) => (items || []).some(it => it.catalogId === "head_scan" || it.catalogId === "body_scan");

/* ═══ SECTION INITIALIZATION ═══ */
export const initSections = () => {
  const s = {};
  Object.entries(SECTION_DEFS).forEach(([k, d]) => {
    // defaultOn sections seed enabled:true — seeding alwaysOn-only would store an
    // explicit enabled:false, which the tri-state read treats as "operator turned
    // this off" and would hide default-on clauses on brand-new quotes.
    s[k] = { enabled: !!(d.alwaysOn || d.defaultOn), text: d.defaultText };
  });
  return s;
};

/* ═══ BLANK TEMPLATES ═══ */
export const DEFAULT_QUOTE_DELIVERABLE_TEXT = "Models delivered textured to the client's specification";

export const defaultFollowUp = (dateStr) => {
  const d = new Date(dateStr || Date.now());
  d.setDate(d.getDate() + 3);
  return d.toISOString().split("T")[0];
};

export const BLANK_QUOTE = () => {
  const quoteDate = new Date().toISOString().split("T")[0];
  return {
    id: "q_" + Date.now(), step: "input", emailText: "", notesText: "",
    industry: "other", leadSource: "referral", jobStatus: "quoted",
    quoteDate, followUpDate: defaultFollowUp(quoteDate),
    referredBy: "", internalNotes: "",
    docType: "quote", clientName: "", contactName: "",
    // The show/project codename ("TGA4", "Lazarus S1") — a SNAPSHOT, never an
    // identity. It must not feed client matching or creation: "Lazarus S1 LLC"
    // the payer and "Lazarus S1" the production normalize to the same string,
    // and keeping the namespaces separate is what stops one from becoming the
    // other (Codex design review, 2026-08-26).
    productionName: "",
    // Structured contact details. Website conversions fill these; the builder's
    // ContactPicker keeps them in step with contactEntityId.
    contactEmail: "", contactPhone: "",
    contactEntityId: null, companyEntityId: null, sourceProspectId: null,
    // A client code approved through the AI box, good only for that client:
    // { code, clientId, clientName } (resolveQuoteClientCode).
    approvedClientCode: null,
    // The box found no CRM client for this name, so no email or domain fallback may link one.
    clientUnlinked: false,
    // Sentences the tool writes itself, per key: null = generated, "" = removed, text =
    // custom. See lib/printWording.js. The money key flags custom wording gone stale.
    printWording: null, printWordingMoneyKey: null,
    jobLocation: "", isStudio: false, scanDate: "", scanDateTBD: false,
    summary: "", turnaround: "3-5", turnaroundTBD: false, turnaroundUnit: "businessDays",
    lidarDetected: false, // AI extraction flag → builder "open the LiDAR estimator" banner
    // Facts the AI read out of the brief for the estimator, one entry per LiDAR block
    // (label + setups, crew, hours, rush). Never a price. Shape per normalizeLidarBlocks();
    // null = nothing stated.
    lidarPrefill: null,
    // Heading note under each line-item group on the PDF, keyed by the group label
    // ({ "Fresh Kills": "Wednesday, October 7" }). See groupLineItems(). Empty = no notes.
    lineGroupNotes: {},
    // The blocks on the quote, in print order: [{ label, note }]. A block can exist
    // with no lines yet (New block). Source of the order and the notes; lineGroupNotes
    // above is derived from it on every write. See normalizeLineGroups(). Empty = none.
    lineGroups: [],
    deliverableText: DEFAULT_QUOTE_DELIVERABLE_TEXT,
    deliverableFormats: ["obj"],
    requiresDeposit: false, includePricingSheet: null,
    // Structured deposit (2026-08-28). Readers go through getDeposit(q), which
    // prefers this object and falls back to legacy requiresDeposit when absent.
    deposit: { type: "none", value: null },
    lineItems: [], aiNotes: "", aiQuestions: [],
    attachments: [], contactMatches: [], quoteDiscounts: [],
    versions: [],
    changeOrderOfJobId: null, changeOrderOfQuoteId: null,
    multiDay: false, sessions: [],
    // Holds (2026-09-04, docs/holds/holds-plan.md). equipmentIds = canonical unit
    // ids (never "equipment" or "selectedEquipment": BLANK_JOB copies those names
    // verbatim into the job's display-name fields). crew = [{ id, name }] with the
    // roster id (lib/holds.js rosterId) and a display snapshot. holdOff false =
    // the "Hold these dates" switch is ON. The hold label is derived, not stored.
    equipmentIds: [], crew: [], holdOff: false,
    sections: initSections(), savedToDb: false
  };
};

// Build the data for a DUPLICATED quote: a brand-new, OPEN quote that keeps the
// source's CONTENT (line items, client, sections, terms, attachments…) but never
// inherits its identity, lifecycle, history, or change-order parentage (money
// audit #2, 2026-06-13). A fresh id + jobStatus:"quoted" drop any booked/lost/
// archived/won state; deletedAt:null drops trash; versions/followUpHistory are
// wiped so the copy can't show a false edit/follow-up trail; changeOrderOf* are
// cleared so it can't mis-book as a change order of the original's parent job.
export const duplicateQuoteData = (saved = {}) => ({
  ...BLANK_QUOTE(), ...saved,
  id: "q_" + Date.now(),
  quoteDate: new Date().toISOString().split("T")[0],
  step: "review", savedToDb: false, jobStatus: "quoted",
  deletedAt: null,
  changeOrderOfJobId: null, changeOrderOfQuoteId: null,
  // A copy of an assessment quote is a plain new quote: kept, the flag would
  // list a never-ingested copy as active (isActiveQuoteRecord).
  fromAssessment: false,
  versions: [], followUpHistory: [],
  lineItems: (saved.lineItems || []).map((li, i) => ({ ...li, key: Date.now() + i })),
  sections: saved.sections || initSections(),
});

// Per-session capture fields (used in multi-day jobs). clientContacts belongs
// here because Capture keeps the client/contact table scoped to the selected day.
export const SESSION_CAPTURE_FIELDS = ["captureLog", "captureNotes", "issues", "actualArrival", "actualWrap", "travelTime", "peoplePresent", "clientContacts"];

// True when a session carries any capture data — drives type-converter gating and delete confirmation.
export const sessionHasCaptureData = (s) => SESSION_CAPTURE_FIELDS.some(f => {
  const v = s?.[f];
  return Array.isArray(v) ? v.length > 0 : (v !== undefined && v !== "" && v !== null);
});

export const normalizeCrew = (crew) =>
  Array.isArray(crew) ? crew
  : (typeof crew === "string" ? crew.split(",").map(s => s.trim()).filter(Boolean) : []);

export const BLANK_SESSION = () => ({
  id: mkSessionId(),
  sessionType: DEFAULT_SESSION_TYPE,
  date: "", dayType: "full", callTime: "", wrapTime: "", notes: "",
  // Per-session location — setup/strike often differ from capture location.
  location: "", locationVenue: "", locationAddress: "",
  locationCity: "", locationState: "", locationZip: "",
  isStudio: false,
  // Capture-only fields — remain unused for setup/strike sessions.
  captureLog: [], captureNotes: "", issues: "",
  actualArrival: "", actualWrap: "", travelTime: "",
  peoplePresent: [], clientContacts: [],
  gcalEventId: null,
  gcalSyncedFingerprint: null,
});

/* ═══ SESSION HELPERS (multi-day jobs) ═══ */
// Derived index for same-date tie-break; SESSION_TYPES array order is canonical (setup, capture, strike).
const SESSION_TYPE_ORDER = Object.fromEntries(SESSION_TYPES.map((t, i) => [t, i]));

// Missing dates sort last; unknown types sort after known types. Array.prototype.sort is stable since ES2019.
export const sortSessions = (sessions) => {
  if (!Array.isArray(sessions)) return [];
  return [...sessions].sort((a, b) => {
    const da = a?.date || "";
    const db = b?.date || "";
    if (da !== db) {
      if (!da) return 1;
      if (!db) return -1;
      return da < db ? -1 : 1;
    }
    const ta = SESSION_TYPE_ORDER[a?.sessionType] ?? 99;
    const tb = SESSION_TYPE_ORDER[b?.sessionType] ?? 99;
    return ta - tb;
  });
};

// Fallback priority: first capture session → first session with any date → job.captureDate.
export const getFirstCaptureDate = (job) => {
  if (!job) return "";
  const sessions = Array.isArray(job.sessions) ? job.sessions : [];
  if (sessions.length > 0) {
    const sorted = sortSessions(sessions);
    const firstCapture = sorted.find(s => s?.sessionType === "capture" && s?.date);
    if (firstCapture) return firstCapture.date;
    const firstAny = sorted.find(s => s?.date);
    if (firstAny) return firstAny.date;
  }
  return job.captureDate || "";
};

// The date an "upcoming" job should anchor on: the soonest session date that is
// today-or-later (so a multi-day job whose first day already passed anchors on its
// NEXT upcoming day, not the past first capture — C1). Falls back to the first
// capture date when no sessions are in the future. `todayStr` is an ISO YYYY-MM-DD
// string; ISO dates compare/sort correctly as plain strings.
export const nextUpcomingSessionDate = (job, todayStr) => {
  const sessions = Array.isArray(job?.sessions) ? job.sessions : [];
  const upcoming = sessions
    .map(s => s?.date)
    .filter(Boolean)
    .filter(d => d >= todayStr)
    .sort();
  return upcoming[0] || getFirstCaptureDate(job);
};

// Falls back to job.captureDate for both bounds when no sessions carry dates.
export const getJobDateRange = (job) => {
  if (!job) return { start: "", end: "" };
  const sessions = Array.isArray(job.sessions) ? job.sessions : [];
  const dates = sessions.map(s => s?.date).filter(Boolean).sort();
  if (dates.length === 0) {
    const fallback = job.captureDate || "";
    return { start: fallback, end: fallback };
  }
  return { start: dates[0], end: dates[dates.length - 1] };
};

// Clash check excludes newSession's own id so editing an existing session doesn't collide with itself.
export const validateSessionAdd = (job, newSession) => {
  if (!newSession || !newSession.date || !newSession.sessionType) {
    return { ok: false, reason: "Session must have a date and type" };
  }
  if (!SESSION_TYPES.includes(newSession.sessionType)) {
    return { ok: false, reason: `Unknown session type: ${newSession.sessionType}` };
  }
  const sessions = (job && Array.isArray(job.sessions)) ? job.sessions : [];
  const clash = sessions.some(s =>
    s && s.id !== newSession.id
    && s.date === newSession.date
    && s.sessionType === newSession.sessionType
  );
  if (clash) {
    return { ok: false, reason: `A ${newSession.sessionType} session already exists on ${newSession.date}` };
  }
  return { ok: true };
};

// Travel section auto-enables whenever any session is on-location. Keeps the client doc
// honest: if the shoot goes anywhere but the studio, the travel terms become visible.
export const applyTravelRule = (sections, sessions) => {
  const anyOnLocation = (sessions || []).some(s => !s?.isStudio);
  const travel = sections?.travel || { enabled: false, text: "" };
  // A switch set on purpose through the AI box stays as it was set (operatorSet).
  if (travel.operatorSet) return { ...sections, travel };
  return { ...sections, travel: { ...travel, enabled: anyOnLocation } };
};

// Shared location-field builders used by both the job-root and session-level Studio toggles.
// Single source of truth for the 7-field expansion — address drift can't reintroduce.
export const studioPatch = () => ({
  isStudio: true,
  location: STUDIO_ADDRESS,
  locationVenue: STUDIO_ADDRESS_PARTS.venue,
  locationAddress: STUDIO_ADDRESS_PARTS.address,
  locationCity: STUDIO_ADDRESS_PARTS.city,
  locationState: STUDIO_ADDRESS_PARTS.state,
  locationZip: STUDIO_ADDRESS_PARTS.zip,
});

export const clearLocationPatch = () => ({
  isStudio: false,
  location: "", locationVenue: "", locationAddress: "",
  locationCity: "", locationState: "", locationZip: "",
});

// Falls back to the freeform `location` string when structured address fields are empty.
// Accepts any shape with location* fields — job or session.
export const addressMapQuery = (obj) => {
  if (!obj) return "";
  const parts = [obj.locationAddress, obj.locationCity, obj.locationState, obj.locationZip].filter(Boolean);
  return parts.join(", ") || obj.location || "";
};

// Default-date logic for "+ Add Day": flight → two days before earliest capture,
// setup → day before earliest capture, strike → day after latest capture,
// capture → day after last capture (or today if no captures yet).
export const defaultDateFor = (type, sessions) => {
  const caps = (sessions || []).filter(s => s?.sessionType === "capture" && s?.date).map(s => s.date).sort();
  const iso = (d) => d.toISOString().split("T")[0];
  const addDays = (dateStr, n) => { const d = new Date(dateStr + "T12:00"); d.setDate(d.getDate() + n); return iso(d); };
  if (type === "flight") return caps[0] ? addDays(caps[0], -2) : "";
  if (type === "setup") return caps[0] ? addDays(caps[0], -1) : "";
  if (type === "strike") return caps[caps.length - 1] ? addDays(caps[caps.length - 1], 1) : "";
  if (caps.length > 0) return addDays(caps[caps.length - 1], 1);
  return iso(new Date());
};

// Pulls a job's current schedule + location into the fields a quote cares about.
// Sessions hydrated through BLANK_SESSION so pre-Phase-1 shapes pick up the new keys.
// Returns a partial object — caller spreads it over the quote (later spread wins).
export const overlayJobIntoQuote = (quote, job) => {
  if (!job) return {};
  const rawSessions = Array.isArray(job.sessions) ? job.sessions : [];
  const sessions = rawSessions.map(s => ({ ...BLANK_SESSION(), ...s }));
  const multiDay = !!job.multiDay && sessions.length > 0;
  const firstCapture = getFirstCaptureDate(job);
  const scanDate = firstCapture || job.captureDate || quote?.scanDate || "";
  return {
    sessions,
    multiDay,
    scanDate,
    scanDateTBD: scanDate ? false : !!quote?.scanDateTBD,
    jobLocation: job.jobLocation || job.location || "",
    isStudio: !!job.isStudio,
  };
};

// The date a job books with (lib/quoteSchedule.js readSchedule reads the same rule).
// A single date marked TBD stays stored behind the switch; it is not a date the client
// was given, so it is not a date to book (the page and the holds already read it so).
// The date a job books with (docs/quotes/quote-saving.md, the schedule): the first
// capture day (a day stored without a type is a capture day, as the builder shows it
// and as holds count it), else the stored single date unless it is marked TBD, else the
// first dated day of any kind. Days count whenever they are stored, as holds do.
export const quoteCaptureDate = (fromQuote) => {
  if (!fromQuote) return "";
  const typed = (Array.isArray(fromQuote.sessions) ? fromQuote.sessions : []).filter(Boolean)
    .map(s => ({ ...s, sessionType: s.sessionType || BLANK_SESSION().sessionType }));
  const sessions = sortSessions(typed);
  const firstCapture = sessions.find(s => s.sessionType === "capture" && s.date);
  const firstAny = sessions.find(s => s.date);
  const single = !fromQuote.scanDateTBD && fromQuote.scanDate && fromQuote.scanDate !== "TBD" ? fromQuote.scanDate : "";
  return firstCapture?.date || single || firstAny?.date || "";
};

export const quoteLocationFields = (fromQuote) => {
  if (fromQuote?.isStudio) {
    return studioPatch();
  }
  const venue = fromQuote?.locationVenue || fromQuote?.jobLocation || "";
  const address = [fromQuote?.locationAddress, fromQuote?.locationCity, fromQuote?.locationState, fromQuote?.locationZip]
    .filter(Boolean)
    .join(", ");
  const location = fromQuote?.jobLocation || [venue, address].filter(Boolean).join(", ");
  return {
    isStudio: false,
    location,
    locationVenue: venue,
    locationAddress: fromQuote?.locationAddress || "",
    locationCity: fromQuote?.locationCity || "",
    locationState: fromQuote?.locationState || "",
    locationZip: fromQuote?.locationZip || "",
  };
};

export const quoteEquipmentList = (fromQuote) => {
  const selected = Array.isArray(fromQuote?.selectedEquipment) ? fromQuote.selectedEquipment : [];
  const raw = selected.length > 0 ? selected : (fromQuote?.equipment || []);
  if (!Array.isArray(raw)) return [];
  return raw.map(eq => {
    if (typeof eq === "string") return eq;
    if (!eq || typeof eq !== "object") return "";
    return eq.name || eq.label || eq.id || "";
  }).filter(Boolean);
};

// Display names for canonical equipment ids through the live id-to-name map
// (lib/equipment.js getEqNames(); the map also carries the retired alias keys).
// An "unknown:<name>" token (lib/holds.js) prints the name behind it; an id the
// map does not know prints as itself. Deduped, in order.
export const equipmentNamesFromIds = (ids, equipmentMap) => {
  const seen = new Set();
  const out = [];
  for (const raw of Array.isArray(ids) ? ids : []) {
    const id = String(raw || "").trim();
    if (!id) continue;
    const mapped = equipmentMap?.[id];
    const name = mapped ? String(mapped).trim() : (id.startsWith("unknown:") ? id.slice("unknown:".length) : id);
    if (name && !seen.has(name)) { seen.add(name); out.push(name); }
  }
  return out;
};

// The names on a quote's tentative crew list ([{ id, name }] or legacy strings).
export const quoteCrewNames = (crew) => {
  const seen = new Set();
  const out = [];
  for (const c of Array.isArray(crew) ? crew : normalizeCrew(crew)) {
    const name = String((typeof c === "string" ? c : c?.name) || "").trim();
    if (name && !seen.has(name)) { seen.add(name); out.push(name); }
  }
  return out;
};

/* The client code a quote books with, the one reader for preview and booking. The
   linked client's own code always wins. A code approved through the AI box
   (approvedClientCode) counts only for the client it was approved for, and only while
   that client has none. The old `clientCode` field on a quote is never read here. */
// The code approved on this quote for exactly this client, when a save fills an empty CRM
// code: the generated one would then outrank it.
export const approvedCodeFor = (quote, client) => {
  const approved = quote?.approvedClientCode;
  return approved?.code && approved.clientId && approved.clientId === client?.id ? approved.code : "";
};

// A linked client with no code takes the job's. Written before the job exists, so a failure
// stops the booking while it can still be tried again: a booked quote can't book a second time.
export const saveClientCodeBeforeBooking = async (job, clients, save) => {
  const client = job?.clientId && job.clientCode ? (clients || []).find((c) => c.id === job.clientId) : null;
  if (!client || client.clientCode) return null;
  const updated = { ...client, clientCode: job.clientCode };
  try {
    await save(updated);
  } catch (e) {
    const err = new Error(`Client code backfill failed for ${client.id}: ${e?.message || e}`);
    err.userMessage = `The client code ${job.clientCode} could not be saved on ${client.name || "this client"}, so the job was not booked. Try again.`;
    throw err;
  }
  return updated;
};

export const resolveQuoteClientCode = (quote, clients) => {
  const { id: explicitId, record: explicit } = readClient(quote, clients);
  // An unlinked decision holds until someone links a client: no match by name.
  const record = explicit || (quote && clients && !quote.clientUnlinked ? resolveClient({ name: quote.clientName }, clients).record : null);
  if (record?.clientCode) return record.clientCode;
  const approved = quote?.approvedClientCode;
  if (approved?.code) {
    const sameClient = approved.clientId
      ? approved.clientId === (record?.id || explicitId)
      : (!record && !explicitId && String(approved.clientName || "") === String(quote?.clientName || ""));
    if (sameClient) return approved.code;
  }
  return mkClientCode(quote?.clientName || "");
};

export const BLANK_JOB = (fromQuote, contacts, clients, opts = {}) => {
  // Client resolution: an EXPLICIT id on the quote (clientId / companyEntityId)
  // is authoritative and must supply BOTH the job's clientId and its clientCode
  // from the same record — deriving the code from the free-typed name while the
  // id points elsewhere gave the job a folder prefix that contradicted the
  // client's established code (Codex review 2026-08-26, F1). Name resolution
  // (identity.js, so " Acme Arts, Inc. " finds "Acme Arts LLC" — CD1) is the
  // fallback only when no explicit id resolves.
  const { id: explicitId, record: explicitRecord } = readClient(fromQuote, clients);
  const namedRecord = (!explicitRecord && fromQuote && clients && !fromQuote.clientUnlinked)
    ? resolveClient({ name: fromQuote.clientName }, clients).record
    : null;
  const cc = resolveQuoteClientCode(fromQuote, clients);
  const resolvedClientId = namedRecord?.id || null;
  const captureDate = quoteCaptureDate(fromQuote);
  const locationFields = quoteLocationFields(fromQuote);
  // Equipment carry-forward (holds, 2026-09-04): the quote's canonical
  // equipmentIds resolve to display names through the live map the caller passes
  // (opts.equipmentMap, bookJob hands over getEqNames()). An array, even an empty
  // one, is the operator's choice; only a record with no equipmentIds field at
  // all reads the legacy name fields (same rule as lib/holds.js quoteEquipmentIds,
  // so the job and the hold can never disagree). Both job copies get names.
  const quoteEquipment = Array.isArray(fromQuote?.equipmentIds)
    ? equipmentNamesFromIds(fromQuote.equipmentIds, opts.equipmentMap)
    : quoteEquipmentList(fromQuote);
  // The tentative crew rides along as names: job crew has always been names
  // (comma string on old rows, an array on new ones; see normalizeCrew).
  const quoteCrew = quoteCrewNames(fromQuote?.crew);

  /* Auto-link contacts from quote data.

     CD8 — this used to fan out: any contact whose free-text `company` string
     equalled the client name got linked to the job (and, in bookJob, promoted to
     "customer"), and an unmatched contact name fell back to a token-subset
     search that read "Rina Patel" as "Rina Patel-Lawson". Booking one quote
     could therefore attach and recategorize a dozen people who had nothing to do
     with it. A persistence path now links only people this quote actually names:
     an explicit id, an exact-name match, the referrer, and whoever the operator
     confirmed in the contact-match panel (`opts.confirmedContactIds`, which is
     where cc'd people arrive). Suggestion-by-similarity belongs in the review
     UI, where a human sees it before it is written. */
  const linkedContacts = [];
  if (fromQuote && contacts) {
    const ctcs = contacts || [];
    const seen = new Set();
    const link = (id, role) => {
      if (!id || seen.has(id)) return;
      linkedContacts.push({ contactId: id, role });
      seen.add(id);
    };

    // An id the operator picked or the commit step created is authoritative.
    link(fromQuote.contactEntityId, "contact");

    // The named contact person — exact match only.
    if (fromQuote.contactName) {
      link(resolveContact({ name: fromQuote.contactName }, ctcs).record?.id, "contact");
    }

    // Everyone confirmed on the quote's contact-match panel, cc'd people included.
    for (const id of opts.confirmedContactIds || []) {
      if (ctcs.some(c => c.id === id)) link(id, "contact");
    }

    // The referrer(s).
    for (const refName of String(fromQuote.referredBy || "").split(",").map(r => r.trim()).filter(Boolean)) {
      link(resolveContact({ name: refName }, ctcs).record?.id, "referral");
    }
  }

  // Resolve clientId: the validated explicit record > name-resolved record > the
  // raw explicit id (kept even when the record isn't in the loaded list, so a
  // slow CRM load can't silently strip a real link).
  const clientId = explicitRecord?.id || resolvedClientId || explicitId || null;
  
  return {
    id: "j_" + Date.now(),
    quoteId: fromQuote?.id || null,
    clientCode: cc,
    jobId: mkJobId(cc, captureDate || ""),
    clientName: fromQuote?.clientName || "",
    contactName: fromQuote?.contactName || "",
    clientId: clientId,
    // The job card shows this as "Production / Project" — filled from the quote's
    // production name, freely editable after booking.
    project: fromQuote?.productionName || "",
    description: fromQuote?.summary || "",
    industry: fromQuote?.industry || "other",
    leadSource: fromQuote?.leadSource || "",
    referredBy: fromQuote?.referredBy || "",
    captureDate: captureDate,
    scanDateTBD: !!fromQuote?.scanDateTBD,
    startTime: "",
    quoteDate: fromQuote?.quoteDate || "",
    turnaround: fromQuote?.turnaround || "",
    turnaroundTBD: !!fromQuote?.turnaroundTBD,
    turnaroundUnit: fromQuote?.turnaroundUnit || null,
    status: "scheduled",
    invoiced: false, paid: false,
    grossRevenue: fromQuote?.total || 0,
    scanningRevenue: 0, processingRevenue: 0,
    productionCost: 0, contractorCost: 0, modeler: "",
    equipment: quoteEquipment, transport: "In Studio",
    ...locationFields,
    backupLocation: "", crew: quoteCrew, prepNotes: "",
    // Pre-Prod / Job Prep fields
    selectedEquipment: [...quoteEquipment],
    captureLog: [], checklist: [], attachments: [],
    lenses: [], additionalItems: [],
    // Capture fields
    callTime: "", wrapTime: "", scheduledTime: "", actualArrival: "", actualWrap: "", travelTime: "",
    captureNotes: "", issues: "",
    peoplePresent: [], clientContacts: [],
    deliverableType: fromQuote?.deliverableText || "textured OBJs",
    deliveryMethod: "box.com",
    deliveryMethods: [],
    deliverableRequirements: "",
    deliveryDate: "", deliveredDate: "", invoiceDate: "", paidDate: "",
    lineItems: fromQuote?.lineItems || [],
    // Quote-level discounts ride along with the lines they discount, so the job
    // card can show the same Subtotal / Pro bono / Total story as the PDF even
    // when the quote is later unlinked (pro bono, 2026-09-11).
    quoteDiscounts: fromQuote?.quoteDiscounts || [],
    // billing: "standard" | "pro_bono" (src/lib/proBono.js). Copied from the
    // quote ONLY when it is pro bono; absent means standard, so a standard
    // booking writes no field and existing jobs need no migration.
    ...(fromQuote?.billing === "pro_bono" ? { billing: "pro_bono" } : {}),
    linkedEntities: [], linkedContacts, files: [], notes: "", jobDocs: [],
    savedThreads: [],
    deliverableFormats: fromQuote?.deliverableFormats || [],
    quotedTerms: fromQuote?.sections || null,
    // Custom printed wording goes with the terms it was quoted under. None writes no field.
    ...(hasCustomWording(fromQuote) ? { printWording: normalizePrintWording(fromQuote.printWording) } : {}),
    changeOrderOf: null, hasChangeOrders: [],
    multiDay: fromQuote?.multiDay || false,
    // Seed per-session location from the quote's job-level location so newly-booked multi-day jobs
    // land with sensible defaults. Session-level values from the quote still win via the rightmost spread.
    sessions: fromQuote?.sessions?.length ? (() => {
      return fromQuote.sessions.map(s => ({ ...BLANK_SESSION(), ...locationFields, ...s }));
    })() : [],
    createdAt: new Date().toISOString(),
  };
};

/* The job-card UI prefers linkedContacts and only falls back to legacy
   linkedEntities when linkedContacts is EMPTY — so the first write that
   creates linkedContacts on a legacy-linked job would silently hide every
   legacy link. Any writer adding to linkedContacts must seed from this:
   existing linkedContacts verbatim, else legacy entities materialized
   ({contactId, role}; billed_client stays client-side, matching the
   read fallback in LinkedContacts.jsx). */
export const seededLinkedContacts = (job) =>
  (job?.linkedContacts || []).length > 0
    ? [...job.linkedContacts]
    : (job?.linkedEntities || [])
        .filter(le => le && le.entityId && le.role !== "billed_client")
        .map(le => ({ contactId: le.entityId, role: le.role || "contact" }));

export const BLANK_CONTACT = () => ({
  id: "ct_" + Date.now(), name: "", emails: [], phone: "", company: "",
  // CD6 — the structural link to a billing entity. `company` above is display
  // text the operator can type freely; `clientId` is the one that survives a
  // rename and joins revenue back to one account.
  clientId: null,
  industry: [], // multi-select array
  role: ["prospect"], // multi-select array, default prospect
  leadSource: "", referredBy: "", score: 0, threadCount: 0,
  firstContact: "", lastContact: "", notes: "",
  mailchimpStatus: "never_sent", lastCampaignDate: "", campaignCount: 0,
  // CRM v3 — the three-state consent record that replaces mailchimpStatus.
  // A blank contact is never bulk-eligible: consent is asserted, not assumed.
  // `source`: "website" | "mailchimp" | "quote" | "job" | "manual" | "migration"
  consent: { state: "no_bulk", source: "", at: "", unsubscribed: false },
  tags: [],
  social: { linkedin: { url: "", connected: false, connectedOn: "" }, instagram: "", x: "", website: "" },
  // The most recent client-facing event, stored so a list can show it without
  // reading the whole interactions collection. Written by interact.mjs,
  // record.mjs and the onInteractionCreated trigger, newest wins.
  // channel: "mail_in" | "mail_out" | "quote" | "job" | "campaign" | "note" | "session"
  lastTouch: { at: "", channel: "", ixId: "", summary: "" },
  linkedClients: [],
});

/* One-level studio association, loop-proof. A client may point at an
   "associated studio" (stored in the pre-existing parentClientId field), but:
   never itself, never a client that itself has a parent (no chains), and never
   an id that doesn't resolve. Returns the client with a safe parentClientId. */
export const sanitizeParentClientId = (client, clients) => {
  const pid = client?.parentClientId || "";
  if (!pid) return client;
  if (pid === client.id) return { ...client, parentClientId: "" };
  const parent = (clients || []).find((c) => c.id === pid);
  if (!parent || parent.parentClientId) return { ...client, parentClientId: "" };
  return client;
};

export const BLANK_CLIENT = () => ({
  id: "cl_" + Date.now(), name: "", clientCode: "", industry: [],
  billingAddress: "", email: "", phone: "",
  status: "active",
  leadSource: "", referredBy: "",
  orgDomain: "", parentClientId: "",
  firstContact: "", lastContact: "", notes: "",
  // CRM v3 — NDA flag. When true, this client's records never enter a model
  // session: the dossier withholds them and the /crm skill does not fetch
  // their mail either. It is a POLICY control our own tools honor, not a
  // technical boundary — anything with Firestore access can still read the
  // record, and Firestore rules do not enforce this flag.
  noModelSessions: false,
  tags: [],
  social: { linkedin: "", instagram: "", x: "", website: "" },
  // Same shape and rules as the contact field above.
  lastTouch: { at: "", channel: "", ixId: "", summary: "" },
  linkedContacts: [],
});

/* CRM v3 slice 4 — a saved audience. Rules, never a list of names: membership
   is computed live every time the segment is opened, so a segment written in
   March is still true in September. No counts are stored for the same reason.

   The consent rule is part of the record and cannot be removed — it is what
   makes the audience bulk-eligible, and src/lib/segments.js applies it again
   whatever the rules say. `source` is who made it: "manual" in the Suite,
   "chat" from tools/crm/segment.mjs. `deletedAt` is a soft delete, so a
   campaign that froze this segment's audience never points at nothing. */
export const BLANK_SEGMENT = () => ({
  id: `sg_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`,
  name: "",
  rules: [{ field: "consent", op: "is", value: [...BULK_ELIGIBLE] }],
  source: "manual",
  createdBy: "",
  createdAt: "",
  updatedAt: "",
  note: "",
});

/* CRM v3 slice 5 — one outreach initiative: its brief, its audience, its copy,
   its approval and its results, in one record. The status machine and every
   rule that reads this shape live in src/lib/campaigns.js.

   `audience.contactIds` is empty until approval freezes it; before that the
   audience is whatever `segmentId` resolves to right now (plan decision 4).
   `send` and `results` are written only by the campaignSend callable — the
   browser and the /crm tools never put anything in them. */
export const BLANK_CAMPAIGN = () => ({
  /* `cp_<epoch ms>_<rand>`, the same scheme campaign.mjs and the Suite use.
     It used to be the bare millisecond, which two campaigns created in the
     same tick would share — and a shared id in this collection is one campaign
     overwriting another's audience. */
  id: makeCampaignId(),
  name: "",
  channel: "email",              // "email" | "one_to_one" | "post"
  status: "draft",
  segmentId: "",
  audience: {
    frozenAt: null,
    contactIds: [],
    counts: { total: 0, opted_in: 0, relationship: 0, no_bulk: 0, held_back: 0 },
  },
  brief: { goal: "", angle: "", exclusions: [], followUp: "", evidence: [] },
  copy: { subject: "", body: "", ctaText: "", ctaUrl: "", versions: [] },
  schedule: { at: null, timezone: "America/New_York" },
  send: { mailchimpCampaignId: "", sentAt: "", error: "" },
  results: { delivered: 0, opened: 0, clicked: 0, replied: 0, unsubscribed: 0, bounced: 0, syncedAt: "" },
  origin: { source: "manual", at: "", by: "", writeId: "" },
  createdAt: "", updatedAt: "", createdBy: "", deletedAt: null,
});

/* ═══ CSV EXPORT ═══ */
// Fields that serialize badly as cells (object arrays, HTML, long bodies).
const CSV_EXCLUDE = new Set(["lineItems", "sections", "emailText", "jobDocs", "quotedTerms", "savedThreads", "sessions", "printWording"]);

// Build CSV string from a list of records. Sessions get expanded into three
// readable columns (count, date range, type sequence) instead of a raw object dump.
// Pure function — no DOM — so it's testable.
export const buildCsv = (data) => {
  if (!data.length) return "";
  const keys = Object.keys(data[0]).filter(k => !CSV_EXCLUDE.has(k));
  const sessionCols = ["session_count", "session_dates", "session_types"];
  const headers = [...keys, ...sessionCols];
  const esc = (v) => `"${String(v == null ? "" : v).replace(/"/g, '""')}"`;
  const rows = [
    headers.join(","),
    ...data.map(r => {
      const sessions = Array.isArray(r.sessions) ? sortSessions(r.sessions) : [];
      const { start, end } = getJobDateRange(r);
      const dateRange = !start ? "" : (start === end ? start : `${start} → ${end}`);
      const types = sessions.map(s => s?.sessionType || "").filter(Boolean).join(", ");
      const derived = [sessions.length || "", dateRange, types];
      return [...keys.map(k => esc(r[k])), ...derived.map(esc)].join(",");
    }),
  ];
  return rows.join("\n");
};

export const exportCsv = (data, name) => {
  const csv = buildCsv(data);
  if (!csv) return;
  const a = document.createElement("a");
  a.href = `data:text/csv;base64,${btoa(unescape(encodeURIComponent(csv)))}`;
  a.download = `${name}.csv`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
};

/* ═══ JOB FIELD VALIDATION (dev-mode) ═══ */
const JOB_FIELDS = new Set([
  "id", "quoteId", "clientCode", "jobId", "clientName", "contactName", "clientId", "project",
  "description", "industry", "captureDate", "startTime", "quoteDate", "status",
  "grossRevenue", "scanningRevenue", "processingRevenue", "productionCost", "contractorCost", "modeler",
  "equipment", "transport", "location", "locationVenue", "locationAddress", "locationCity", "locationState", "locationZip", "isStudio",
  "backupLocation", "crew", "prepNotes", "assignedTo",
  "selectedEquipment", "captureLog", "captureLogDeleted", "checklist", "attachments", "lenses", "additionalItems",
  "callTime", "wrapTime", "scheduledTime", "actualArrival", "actualWrap", "travelTime", "captureNotes", "issues", "peoplePresent", "clientContacts",
  "deliverableType", "deliveryMethod", "deliveryMethods", "deliverableRequirements", "deliveredDate", "invoiced", "paid", "invoiceDate", "paidDate",
  "lineItems", "linkedEntities", "linkedContacts", "files", "notes", "jobDocs", "multiDay", "sessions", "createdAt",
  "changeOrderOf", "hasChangeOrders", "deletedAt", "deliveryDate",
  "savedThreads", "deliverableFormats", "quotedTerms", "printWording",
  "gcalEventId", "gcalSyncedFingerprint",
  "billing", "quoteDiscounts",
]);

export const HYDRATED_SESSION_ID_PREFIX = "sess_hydrated_";
export const hydratedSessionId = (jobId) => `${HYDRATED_SESSION_ID_PREFIX}${jobId}`;
export const isHydratedSessionId = (id) => typeof id === "string" && id.startsWith(HYDRATED_SESSION_ID_PREFIX);

// Normalizes a Firestore job record into its in-memory shape.
// - Status/milestone backfill for pre-multiday records.
// - Multi-day sessions get missing location/sessionType fields backfilled.
// - Single-day jobs get a hydrated sessions[0] synthesized from root schedule/location fields.
//   Invariant: a single-day job has either zero sessions or exactly one hydrated session
//   (id prefix `sess_hydrated_`). Any other shape is legacy data we don't touch.
// Idempotent: returns the same object reference if nothing needs to change.
export const migrateJob = (j) => {
  let m = j;
  if (j.status === "pre_scan") m = { ...m, status: "scheduled" };
  if (j.status === "invoiced") m = { ...m, status: "delivered", invoiced: true, paid: false };
  if (j.status === "paid") m = { ...m, status: "delivered", invoiced: true, paid: true };
  if (m.invoiced === undefined) m = { ...m, invoiced: false };
  if (m.paid === undefined) m = { ...m, paid: false };
  if (m.multiDay && Array.isArray(m.sessions) && m.sessions.length > 0) {
    let changed = false;
    const nextSessions = m.sessions.map(s => {
      if (!s || typeof s !== "object") return s;
      let next = s;
      if (!next.id) { next = { ...next, id: mkSessionId() }; changed = true; }
      if (!next.sessionType) { next = { ...next, sessionType: DEFAULT_SESSION_TYPE }; changed = true; }
      // hasOwnProperty distinguishes "field missing" (backfill) from "user cleared to empty" (leave alone).
      for (const f of SESSION_LOCATION_FIELDS) {
        if (!Object.prototype.hasOwnProperty.call(next, f)) {
          next = { ...next, [f]: m[f] || "" };
          changed = true;
        }
      }
      if (!Object.prototype.hasOwnProperty.call(next, "isStudio")) {
        next = { ...next, isStudio: !!m.isStudio };
        changed = true;
      }
      return next;
    });
    if (changed) m = { ...m, sessions: nextSessions };
  } else if (!m.multiDay) {
    const existingSessions = Array.isArray(m.sessions) ? m.sessions : [];
    const hydratedId = hydratedSessionId(m.id);
    const hasHydrated = existingSessions.length === 1 && existingSessions[0]?.id === hydratedId;
    const shouldHydrate = !!m.captureDate && (existingSessions.length === 0 || hasHydrated);
    const shouldClear = !m.captureDate && hasHydrated;
    if (shouldHydrate) {
      const existing = hasHydrated ? existingSessions[0] : null;
      const desired = {
        date: m.captureDate || "",
        callTime: m.callTime || "",
        wrapTime: m.wrapTime || "",
        notes: m.notes || "",
        isStudio: !!m.isStudio,
        gcalEventId: m.gcalEventId || null,
        gcalSyncedFingerprint: m.gcalSyncedFingerprint || null,
      };
      for (const f of SESSION_LOCATION_FIELDS) desired[f] = m[f] || "";
      const scalarFields = ["date", "callTime", "wrapTime", "notes", "isStudio", ...SESSION_LOCATION_FIELDS];
      const matches = existing
        && existing.sessionType === "capture"
        && scalarFields.every(f => existing[f] === desired[f])
        && (existing.gcalEventId || null) === desired.gcalEventId
        && (existing.gcalSyncedFingerprint || null) === desired.gcalSyncedFingerprint;
      if (!matches) {
        m = { ...m, sessions: [{ ...BLANK_SESSION(), id: hydratedId, sessionType: "capture", ...desired }] };
      }
    } else if (shouldClear) {
      m = { ...m, sessions: [] };
    }
  }
  return m;
};

// Normalizes a Firestore quote record into its in-memory shape. Backfills the turnaround
// unit: a quote saved before turnaround switched from weeks → business days has no
// `turnaroundUnit`, and legacy numbers meant WEEKS, so stamp "weeks" once at load. New-schema
// quotes already carry "businessDays" and are left alone. Doing this at the single load
// boundary (like migrateJob) means no hydration path — open/duplicate/amend/draft — can
// re-leak BLANK_QUOTE's "businessDays" default onto a legacy quote.
// Idempotent: returns the same object reference if nothing needs to change.
// Also stamps the structured deposit on legacy quotes (2026-08-28): a record with
// no `deposit` object gets one derived from the legacy requiresDeposit boolean,
// marked legacy:true so buildPaymentSentence keeps the historical sentence
// verbatim. Doing it here (the single load boundary) means no hydration path —
// open/duplicate/amend — can leak BLANK_QUOTE's fresh {type:"none"} onto a
// legacy quote and silently erase its deposit.
export const migrateQuote = (q) => {
  if (!q) return q;
  let m = q;
  if (m.turnaroundUnit == null) m = { ...m, turnaroundUnit: "weeks" };
  if (!m.deposit || typeof m.deposit !== "object" || !m.deposit.type) {
    m = m === q ? { ...m } : m;
    m.deposit = { type: m.requiresDeposit ? "full_session" : "none", value: null, legacy: true };
  }
  // Holds (2026-09-04): a quote saved before equipmentIds existed carries its
  // equipment as display names under the legacy fields. Stamp those names into
  // equipmentIds once, here, so no hydration path can spread BLANK_QUOTE's empty
  // list over them; every reader normalizes the list through the live equipment
  // map (lib/holds.js normalizeEquipmentIds), and the next Ingest persists ids.
  if (!Array.isArray(m.equipmentIds)) {
    const legacy = quoteEquipmentList(m);
    if (legacy.length) {
      m = m === q ? { ...m } : m;
      m.equipmentIds = legacy;
    }
  }
  return m;
};


export const validateJobFields = (updates) => {
  if (import.meta.env?.DEV) {
    for (const key of Object.keys(updates)) {
      if (!JOB_FIELDS.has(key)) {
        console.warn(`⚠ Unknown job field: "${key}". Did you mean one of: ${[...JOB_FIELDS].filter(f => f.includes(key.slice(0, 4))).join(", ") || "(no close matches)"}`);
      }
    }
  }
};

export const BLANK_DOC = () => ({
  id: "d_" + Date.now(),
  title: "",
  category: "general", // site_requirements | pre_scan | scope_of_work | general
  content: "",         // the document body (HTML string for Tiptap)
  clientName: "",
  contactName: "",
  jobId: "",           // optional link to a job
  jobLocation: "",
  folderId: null,      // null = unfiled / root level
  confidential: true,  // show confidentiality notice on PDF
  isTemplate: false,   // if true, shows in template library
  templateName: "",    // display name when used as template
  sharedWithClient: false,  // manual checkbox — has this been sent?
  usedBlocks: [],      // [{ blockId, version }] — content blocks used in generation
  // DocuSign e-signature fields
  signatureStatus: null,    // null | "sent" | "delivered" | "viewed" | "completed" | "declined" | "voided"
  docusignEnvelopeId: null, // DocuSign envelope ID string
  signerName: "",           // who signs
  signerEmail: "",          // signer's email
  createdAt: new Date().toISOString(),
  updatedAt: "",
});

/* ═══ CONTENT BLOCK (Templates → Blocks view) ═══ */
export const BLANK_CONTENT_BLOCK = () => ({
  id: "cb_" + Date.now(),
  title: "",
  content: "",           // HTML string
  tags: [],              // e.g. ["studio", "body_scan", "wardrobe"]
  conditions: {},        // { serviceTypes: [], locationTypes: [] }
  archived: false,
  version: 1,
  contentHash: "",
  versions: [],          // [{ version, content, contentHash, updatedAt }]
  usedByTemplates: [],   // [docTemplateId, ...] — deletion protection
  createdAt: new Date().toISOString(),
  updatedAt: "",
});

/* ═══ DOCUMENT TEMPLATE (Templates → Documents view) ═══ */
export const BLANK_DOC_TEMPLATE = () => ({
  id: "dt_" + Date.now(),
  name: "",              // "Project Brief", "Billing Agreement", etc.
  lifecycleStage: "",    // "post_booking", "day_before", "post_shoot", "post_delivery"
  docType: "",           // "billing_agreement", "project_brief", etc.
  sections: [],          // [{ title, blockId?, placeholder?, aiPrompt?, order }]
  blockRefs: [],         // [blockId, ...] — which content blocks this template uses
  createdAt: new Date().toISOString(),
  updatedAt: "",
});

/* ═══ CONTENT HASH (for block version tracking) ═══ */
export const computeContentHash = (str) => {
  let hash = 0;
  const s = (str || "").trim();
  for (let i = 0; i < s.length; i++) {
    hash = ((hash << 5) - hash + s.charCodeAt(i)) | 0;
  }
  return hash.toString(36);
};

export const BLANK_CONVERSATION = () => ({
  id: "conv_" + Date.now(),
  title: "",
  contactIds: [],
  clientIds: [],
  jobIds: [],
  threadIds: [],
  messages: [],        // [{ role: "user"|"assistant", content, timestamp, actionCards? }]
  actions: [],         // [{ type: "email_draft"|"template_doc"|"job_doc"|"task", itemId, title, timestamp }]
  model: "claude-opus-4-6",
  createdAt: new Date().toISOString(),
  updatedAt: "",
  createdBy: "",
});

export const BLANK_TASK = () => ({
  id: "task_" + Date.now(),
  text: "",
  type: "task",        // "task" | "note" | "idea"
  status: "open",      // "open" | "completed"
  dueDate: null,
  completedAt: null,
  contactIds: [],
  clientIds: [],
  jobIds: [],
  conversationId: null,
  createdAt: new Date().toISOString(),
  updatedAt: "",
  createdBy: "",
});

export const BLANK_FEEDBACK = () => ({
  id: "fb_" + Date.now(),
  tool: "",              // "project_planner" | "pricing_engine" | "vision_analyzer" | "quote_builder" | "other"
  title: "",             // Short description of the issue
  issue: "",             // What went wrong — specific, factual
  expected: "",          // What should have happened
  suggestedFix: "",      // How the tool's logic should change
  linkedIds: {},         // { assessmentId, quoteId, jobId } — optional links
  conversationId: null,  // AI conversation where feedback was discussed
  status: "open",        // "open" | "applied" | "dismissed"
  createdAt: new Date().toISOString(),
  updatedAt: "",
  createdBy: "",
});

/* ═══ LEGACY TEXT → HTML MIGRATION ═══ */
export const textToHtml = (text) => {
  if (!text) return "";
  return text.split("\n\n").map(para => {
    const t = para.trim();
    if (!t) return "";
    // Header detection (same heuristics as legacy preview renderer)
    const isHeader = t.length < 80 && (
      (t === t.toUpperCase() && t.length > 2 && /[A-Z]/.test(t)) ||
      t.endsWith(":") ||
      /^#{1,3}\s/.test(t)
    );
    if (isHeader) return `<h2>${t.replace(/^#{1,3}\s*/, "").replace(/:$/, "")}</h2>`;
    // Bullet list
    if (t.startsWith("- ") || t.startsWith("• ") || t.includes("\n- ") || t.includes("\n• ")) {
      const items = t.split("\n").filter(l => /^[-•]\s/.test(l.trim()));
      return `<ul>${items.map(l => `<li>${l.trim().replace(/^[-•]\s*/, "")}</li>`).join("")}</ul>`;
    }
    // Numbered list
    if (/^\d+[.)]\s/.test(t)) {
      const items = t.split("\n").filter(l => /^\d+[.)]\s/.test(l.trim()));
      return `<ol>${items.map(l => `<li>${l.trim().replace(/^\d+[.)]\s*/, "")}</li>`).join("")}</ol>`;
    }
    return `<p>${t.replace(/\n/g, "<br>")}</p>`;
  }).filter(Boolean).join("");
};
